import axios from 'axios';
import { plainToClass } from 'class-transformer';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useModalStore } from './modal';
import injector from '@/helpers/httpInterceptor';
import { wrap } from '@/helpers/objectWrapper';
import { User } from '@/shared/models/User';

export interface AuthState {
  user?: User
  token: string
  status: string
}

export const useAuthStore = defineStore('auth', () => {
    // State using ref
    const user = ref<User | undefined>(
        plainToClass(User, JSON.parse(localStorage.getItem('auth-user') || '{}')) || undefined,
    );
    const token = ref(localStorage.getItem('user-token') || '');
    const status = ref('');
    const tokenValidated = ref(false);

    // Getters using computed
    const isAuthenticated = computed((): boolean => !!token.value && tokenValidated.value);
    const authStatus = computed((): string => status.value);
    const authUser = computed((): User | undefined => user.value);
    const authToken = computed((): string => token.value);

    // Actions as regular functions
    function setLoading(loading: boolean) {
        const modalStore = useModalStore();
        if (loading) {
            modalStore.startLoading();
        } else {
            modalStore.stopLoading();
        }
    }

    function setAuthRequest() {
        status.value = 'loading';
    }

    function setAuthSuccess(tokenValue: string) {
        status.value = 'success';
        token.value = tokenValue;
        tokenValidated.value = true;
        injector();
    }

    function setAuthError() {
        status.value = 'error';
    }

    function setAuthUser(userValue: User) {
        user.value = userValue;
    }

    async function register(formData: any) {
        setLoading(true);
        try {
            setAuthRequest();
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL_V2}contacts`, wrap(formData));
            setLoading(false);
            return response;
        } catch (error) {
            setLoading(false);
            throw error;
        }
    }

    async function login(formData: any) {
        setLoading(true);
        try {
            setAuthRequest();
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL_V2}authenticate`, formData);
      
            const tokenValue = response.data.data.attributes.token;
            localStorage.setItem('user-token', tokenValue);

            const attributes = response.data.data.attributes;
            const userValue = plainToClass(User, attributes);
            localStorage.setItem('auth-user', JSON.stringify(userValue));

            setLoading(false);
            setAuthUser(userValue);
            setAuthSuccess(tokenValue);
            return response;
        } catch (error) {
            setLoading(false);
            setAuthError();
            logout();
            throw error;
        }
    }

    async function loginWithToken(formData: any) {
        setLoading(true);
        try {
            setAuthRequest();
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL_V2}authenticate-with-token`, formData);
      
            const tokenValue = response.data.data.attributes.token;
            localStorage.setItem('user-token', tokenValue);

            const attributes = response.data.data.attributes;
            const userValue = plainToClass(User, attributes);
            localStorage.setItem('auth-user', JSON.stringify(userValue));

            setLoading(false);
            setAuthUser(userValue);
            setAuthSuccess(tokenValue);
            return response;
        } catch (error) {
            setLoading(false);
            setAuthError();
            logout();
            throw error;
        }
    }

    function logout() {
        token.value = '';
        user.value = undefined;
        tokenValidated.value = false;
        localStorage.removeItem('user-token');
        localStorage.removeItem('auth-user');
        return Promise.resolve(true);
    }

    function setUser(userValue: User) {
        setAuthUser(userValue);
        localStorage.setItem('auth-user', JSON.stringify(userValue));
        return Promise.resolve(true);
    }

    async function forgotPassword(formData: any) {
        setLoading(true);
        try {
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL_V2}password/email`, formData);
            setLoading(false);
            return response;
        } catch (error) {
            setLoading(false);
            setAuthError();
            throw error;
        }
    }

    async function resetPassword(formData: any) {
        setLoading(true);
        try {
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL_V2}password/reset`, formData);
            setLoading(false);
            return response;
        } catch (error) {
            setLoading(false);
            setAuthError();
            throw error;
        }
    }

    async function getAccountInfo() {
        try {
            const response = await axios.get(`${import.meta.env.VUE_APP_API_URL}bestelsite/account`);
            return response;
        } catch (error) {
            setAuthError();
            throw error;
        }
    }

    async function validateToken() {
        if (!token.value) {
            tokenValidated.value = false;
            return false;
        }
    
        try {
            await getAccountInfo();
            tokenValidated.value = true;
            return true;
        } catch (error) {
            // Token is invalid, clear auth state
            logout();
            return false;
        }
    }

    async function changePassword(formData: any) {
        setLoading(true);
        try {
            const response = await axios.put(`${import.meta.env.VUE_APP_API_URL}bestelsite/account/password`, formData);
            setLoading(false);
            return response;
        } catch (error) {
            setLoading(false);
            setAuthError();
            throw error;
        }
    }

    return {
    // State
        user,
        token,
        status,
        tokenValidated,
        // Getters
        isAuthenticated,
        authStatus,
        authUser,
        authToken,
        // Actions
        setLoading,
        setAuthRequest,
        setAuthSuccess,
        setAuthError,
        setAuthUser,
        register,
        login,
        loginWithToken,
        logout,
        setUser,
        forgotPassword,
        resetPassword,
        getAccountInfo,
        validateToken,
        changePassword,
    };
}); 
